// src/components/resources/guides/content/ContentBlocks.tsx

'use client'

import Image from 'next/image'
import Link from 'next/link'
import type { ContentBlock, CalloutVariant } from '~/lib/resources/types/guides/content'
import { RESOURCE_TYPE_PLURALS } from '~/lib/resources/types/shared/enums'
import { cn } from '~/lib/utils'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import RiCheckboxCircleLine from '~/icons/remix/generated/RiCheckboxCircleLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiAlertLine from '~/icons/remix/generated/RiAlertLine'
import RiErrorWarningLine from '~/icons/remix/generated/RiErrorWarningLine'
import RiShieldCheckLine from '~/icons/remix/generated/RiShieldCheckLine'

interface ContentBlocksProps {
  blocks: ContentBlock[]
}

const CALLOUT_STYLES: Record<
  CalloutVariant,
  { border: string; bg: string; icon: typeof RiInformationLine; iconColor: string }
> = {
  info: {
    border: 'border-blue-200/70 dark:border-blue-800/40',
    bg: 'bg-blue-50/60 dark:bg-blue-950/30',
    icon: RiInformationLine,
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  warning: {
    border: 'border-amber-200/70 dark:border-amber-800/40',
    bg: 'bg-amber-50/60 dark:bg-amber-950/30',
    icon: RiAlertLine,
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  tip: {
    border: 'border-emerald-200/70 dark:border-emerald-800/40',
    bg: 'bg-emerald-50/60 dark:bg-emerald-950/30',
    icon: RiLightbulbLine,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  caution: {
    border: 'border-orange-200/70 dark:border-orange-800/40',
    bg: 'bg-orange-50/60 dark:bg-orange-950/30',
    icon: RiErrorWarningLine,
    iconColor: 'text-orange-600 dark:text-orange-400',
  },
  success: {
    border: 'border-indigo-200/70 dark:border-indigo-800/40',
    bg: 'bg-indigo-50/60 dark:bg-indigo-950/30',
    icon: RiShieldCheckLine,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
  },
}

/**
 * Renders inline markdown emphasis and links within a paragraph.
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold text-gray-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      )
    }
    const linkPattern = /^\[([^\]]+)\]\(([^)]+)\)$/
    const linkMatch = linkPattern.exec(part)
    if (linkMatch?.[1] && linkMatch[2]) {
      const href = linkMatch[2]
      const isExternal = href.startsWith('http')
      return (
        <Link
          key={index}
          href={href}
          className="font-medium text-indigo-600 underline decoration-indigo-300 underline-offset-2 hover:text-indigo-500 dark:text-indigo-400"
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {linkMatch[1]}
        </Link>
      )
    }
    return part
  })
}

/**
 * Renders a simple markdown string with paragraphs and bullet lists.
 */
function MarkdownBlock({ markdown }: { markdown: string }): JSX.Element {
  const segments = markdown.split('\n\n')

  return (
    <div className="space-y-4 text-base leading-relaxed text-gray-600 dark:text-gray-400">
      {segments.map((segment, index) => {
        const trimmed = segment.trim()
        if (!trimmed) return null

        // Heading: a single `#`-prefixed line. Without this the renderer falls
        // through to a paragraph and the literal `###` markers show on the page.
        // Map deeper levels to <h4> and shallower ones to <h3> (the section
        // title is already the <h2> on the page).
        const headingMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed)
        if (headingMatch && !trimmed.includes('\n')) {
          const level = headingMatch[1]?.length ?? 3
          const text = headingMatch[2] ?? ''
          const Heading = level >= 3 ? 'h4' : 'h3'
          return (
            <Heading
              key={index}
              className={cn(
                'font-semibold text-gray-900 dark:text-white',
                level >= 3 ? 'text-base' : 'text-lg',
              )}
            >
              {renderInlineMarkdown(text)}
            </Heading>
          )
        }

        if (trimmed.startsWith('- ')) {
          const items = trimmed.split('\n').filter((line) => line.startsWith('- '))
          return (
            <ul key={index} className="list-disc space-y-2 pl-5">
              {items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInlineMarkdown(item.replace(/^- /, ''))}</li>
              ))}
            </ul>
          )
        }

        // Ordered list: a segment whose lines are `1. `, `2. `, … Without this,
        // numbered methodology/how-to steps collapse into one paragraph with
        // inline numbers (the renderer otherwise only special-cases bullets).
        const orderedItem = /^\d+\.\s+/
        if (orderedItem.test(trimmed)) {
          const items = trimmed.split('\n').filter((line) => orderedItem.test(line.trim()))
          return (
            <ol key={index} className="list-decimal space-y-2 pl-5">
              {items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInlineMarkdown(item.trim().replace(orderedItem, ''))}</li>
              ))}
            </ol>
          )
        }

        return (
          <p key={index}>{renderInlineMarkdown(trimmed)}</p>
        )
      })}
    </div>
  )
}

function CalloutBlock({
  variant,
  title,
  body,
}: {
  variant: CalloutVariant
  title: string
  body: string
}): JSX.Element {
  const style = CALLOUT_STYLES[variant]
  const Icon = style.icon

  return (
    <aside
      className={cn(
        'rounded-xl border p-5 sm:p-6',
        style.border,
        style.bg,
      )}
    >
      <div className="flex gap-3">
        <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', style.iconColor)} aria-hidden />
        <div className="min-w-0 space-y-2">
          <p className="font-semibold text-gray-900 dark:text-white">{title}</p>
          <MarkdownBlock markdown={body} />
        </div>
      </div>
    </aside>
  )
}

function StepsBlock({
  steps,
}: {
  steps: Array<{ title: string; description: string; tip?: string }>
}): JSX.Element {
  return (
    <ol className="space-y-6">
      {steps.map((step, index) => (
        <li key={step.title} className="relative flex gap-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white dark:bg-indigo-500">
            {index + 1}
          </span>
          <div className="min-w-0 space-y-2 pt-0.5">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{step.title}</h4>
            <p className="text-base text-gray-600 dark:text-gray-400">{step.description}</p>
            {step.tip && (
              <p className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <span className="font-medium text-indigo-600 dark:text-indigo-400">Tip: </span>
                {step.tip}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}

function ChecklistBlock({
  title,
  items,
}: {
  title?: string
  items: Array<{ text: string; detail?: string }>
}): JSX.Element {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900/50 sm:p-6">
      {title && (
        <h4 className="mb-4 font-semibold text-gray-900 dark:text-white">{title}</h4>
      )}
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item.text} className="flex gap-3">
            <RiCheckboxCircleLine
              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500"
              aria-hidden
            />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{item.text}</p>
              {item.detail && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{item.detail}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ComparisonTableBlock({
  headers,
  rows,
}: {
  headers: [string, string, string]
  rows: Array<[string, string, string]>
}): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
        <thead className="bg-gray-50 dark:bg-gray-900/80">
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                scope="col"
                className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-gray-950/50">
          {rows.map((row) => (
            <tr key={row[0]} className="hover:bg-gray-50/80 dark:hover:bg-gray-900/40">
              {row.map((cell, cellIndex) => (
                <td
                  key={`${row[0]}-${cellIndex}`}
                  className="px-4 py-3 text-gray-600 dark:text-gray-400"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Arbitrary-width data table with inline-markdown cells. Used for the
 * citation-rich benchmark tables where each cell may contain `**bold**` and
 * `[label](href)` source links. Scrolls horizontally on narrow viewports.
 */
function DataTableBlock({
  caption,
  headers,
  rows,
  note,
  emphasizeFirstColumn,
}: {
  caption?: string
  headers: string[]
  rows: string[][]
  note?: string
  emphasizeFirstColumn?: boolean
}): JSX.Element {
  return (
    <figure className="space-y-2">
      {caption && (
        <figcaption className="text-sm font-semibold text-gray-900 dark:text-white">
          {renderInlineMarkdown(caption)}
        </figcaption>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-gray-900/80">
            <tr>
              {headers.map((header, headerIndex) => (
                <th
                  key={`${header}-${headerIndex}`}
                  scope="col"
                  className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 dark:text-white"
                >
                  {renderInlineMarkdown(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-gray-950/50">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="align-top hover:bg-gray-50/80 dark:hover:bg-gray-900/40">
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={cn(
                      'px-4 py-3 text-gray-600 dark:text-gray-400',
                      emphasizeFirstColumn &&
                        cellIndex === 0 &&
                        'font-medium text-gray-900 dark:text-gray-100',
                    )}
                  >
                    {renderInlineMarkdown(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {note && (
        <figcaption className="text-xs text-gray-500 dark:text-gray-400">
          {renderInlineMarkdown(note)}
        </figcaption>
      )}
    </figure>
  )
}

function ImageBlock({
  src,
  alt,
  caption,
  width = 2400,
  height = 1350,
}: {
  src: string
  alt: string
  caption?: string
  width?: number
  height?: number
}): JSX.Element {
  return (
    <figure className="my-2">
      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          sizes="(min-width: 768px) 768px, 100vw"
          className="h-auto w-full"
        />
      </div>
      {caption && (
        <figcaption className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
          {renderInlineMarkdown(caption)}
        </figcaption>
      )}
    </figure>
  )
}

function StatGridBlock({
  stats,
}: {
  stats: Array<{ label: string; value: string; detail?: string }>
}): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="rounded-xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/80 to-white p-5 dark:border-indigo-800/40 dark:from-indigo-950/40 dark:to-gray-900/50"
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
            {stat.label}
          </p>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
          {stat.detail && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{stat.detail}</p>
          )}
        </div>
      ))}
    </div>
  )
}

function ToolCtaBlock({
  toolSlug,
  toolType,
  title,
  description,
}: {
  toolSlug: string
  toolType: 'generator' | 'calculator' | 'analyzer' | 'quiz'
  title: string
  description: string
}): JSX.Element {
  const folder = RESOURCE_TYPE_PLURALS[toolType] ?? `${toolType}s`
  const href = `/resources/tools/${folder}/${toolSlug}`

  return (
    <Link
      href={href}
      className="group flex items-center justify-between gap-4 rounded-xl border border-indigo-200/70 bg-indigo-50/50 p-5 transition-all hover:border-indigo-300 hover:bg-indigo-50 hover:shadow-md dark:border-indigo-800/50 dark:bg-indigo-950/30 dark:hover:border-indigo-600 dark:hover:bg-indigo-950/50"
    >
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
          Free Tool
        </p>
        <p className="mt-1 font-semibold text-gray-900 group-hover:text-indigo-700 dark:text-white dark:group-hover:text-indigo-300">
          {title}
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
      </div>
      <RiArrowRightLine
        className="h-5 w-5 shrink-0 text-indigo-600 transition-transform group-hover:translate-x-1 dark:text-indigo-400"
        aria-hidden
      />
    </Link>
  )
}

function CitationBlock({
  text,
  source,
  sourceUrl,
}: {
  text: string
  source: string
  sourceUrl: string
}): JSX.Element {
  return (
    <blockquote className="border-l-4 border-gray-300 pl-5 dark:border-gray-700">
      <p className="text-base italic text-gray-700 dark:text-gray-300">&ldquo;{text}&rdquo;</p>
      <footer className="mt-2 text-sm text-gray-500 dark:text-gray-500">
        —{' '}
        <Link
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          {source}
        </Link>
      </footer>
    </blockquote>
  )
}

function KeyTakeawayBlock({ items }: { items: string[] }): JSX.Element {
  return (
    <div className="rounded-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 p-6 dark:border-indigo-800/60 dark:from-indigo-950/50 dark:to-violet-950/30">
      <p className="text-xs font-bold uppercase tracking-widest text-indigo-700 dark:text-indigo-300">
        Key Takeaways
      </p>
      <ul className="mt-4 space-y-2">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-gray-800 dark:text-gray-200">
            <span className="text-indigo-600 dark:text-indigo-400" aria-hidden>
              ✓
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Renders an ordered list of content blocks for a guide section.
 *
 * @param {ContentBlocksProps} props - Block list to render.
 * @returns {JSX.Element} Rendered content blocks.
 */
export function ContentBlocks({ blocks }: ContentBlocksProps): JSX.Element {
  return (
    <div className="space-y-6">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'markdown':
            return <MarkdownBlock key={index} markdown={block.markdown} />
          case 'callout':
            return (
              <CalloutBlock
                key={index}
                variant={block.variant}
                title={block.title}
                body={block.body}
              />
            )
          case 'steps':
            return <StepsBlock key={index} steps={block.steps} />
          case 'checklist':
            return (
              <ChecklistBlock key={index} title={block.title} items={block.items} />
            )
          case 'comparison-table':
            return (
              <ComparisonTableBlock
                key={index}
                headers={block.headers}
                rows={block.rows}
              />
            )
          case 'data-table':
            return (
              <DataTableBlock
                key={index}
                caption={block.caption}
                headers={block.headers}
                rows={block.rows}
                note={block.note}
                emphasizeFirstColumn={block.emphasizeFirstColumn}
              />
            )
          case 'image':
            return (
              <ImageBlock
                key={index}
                src={block.src}
                alt={block.alt}
                caption={block.caption}
                width={block.width}
                height={block.height}
              />
            )
          case 'stat-grid':
            return <StatGridBlock key={index} stats={block.stats} />
          case 'tool-cta':
            return (
              <ToolCtaBlock
                key={index}
                toolSlug={block.toolSlug}
                toolType={block.toolType}
                title={block.title}
                description={block.description}
              />
            )
          case 'citation':
            return (
              <CitationBlock
                key={index}
                text={block.text}
                source={block.source}
                sourceUrl={block.sourceUrl}
              />
            )
          case 'key-takeaway':
            return <KeyTakeawayBlock key={index} items={block.items} />
          default: {
            const _exhaustive: never = block
            return _exhaustive
          }
        }
      })}
    </div>
  )
}
